import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Rating,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { getDatabase, ref, update, push } from 'firebase/database';

const AutoReviewPrompt = ({ open, onClose, appointment, patientId, patientName, doctorId }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Check if appointment date is in the past (completed)
  const isCompleted = () => {
    if (!appointment?.date) return false;
    const [month, day, year] = appointment.date.split('/');
    const appointmentDate = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return appointmentDate < today;
  };

  const handleSubmit = async () => {
    if (rating === 0) return;

    setLoading(true);
    try {
      const db = getDatabase();
      const reviewId = push(ref(db, `reviews/${doctorId}`)).key;
      
      const reviewData = {
        patientId,
        patientName,
        appointmentId: appointment.id,
        rating,
        comment: comment.trim() || 'No comment provided',
        date: new Date().toISOString(),
        doctorId
      };

      await update(ref(db, `reviews/${doctorId}/${reviewId}`), reviewData);
      await update(ref(db, `users/${patientId}/appointments/${appointment.id}`), {
        reviewed: true,
        reviewId
      });

      setSubmitted(true);
      setTimeout(() => {
        onClose();
        setRating(0);
        setComment('');
        setSubmitted(false);
      }, 2000);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <span style={{ fontSize: 28 }}>💬</span>
          <Typography variant="h6" fontWeight="bold">
            How was your appointment?
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {submitted ? (
          <Alert severity="success">Thank you for your feedback! 🎉</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Your appointment with <strong>{appointment?.doctorName}</strong> has been completed.
              Please share your experience.
            </Typography>

            <Box textAlign="center" my={3}>
              <Rating
                value={rating}
                onChange={(e, v) => setRating(v)}
                size="large"
                sx={{ fontSize: 48 }}
              />
            </Box>

            <TextField
              label="Write your review"
              multiline
              rows={3}
              fullWidth
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share details about your experience..."
            />
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Later
        </Button>
        {!submitted && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={rating === 0 || loading}
            sx={{ bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' } }}
          >
            {loading ? <CircularProgress size={24} /> : 'Submit Review'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AutoReviewPrompt;